import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { App } from "antd";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import apiClient from "@/app/api/client";
import { usePaginatedQuery } from "@/shared/hooks/usePaginatedQuery";
import { getErrorMessage } from "@/shared/utils/getErrorMessage";
import { attendanceService } from "../services/attendance.service";
import { useRecomputeStore } from "../store/recompute.store";
import type { AttendanceDay, AttendanceDayDetail, CorrectionInput } from "../types";
import { recomputePollInterval } from "../utils/recompute";

export interface AttendanceDayFilters {
	from: string;
	to: string;
	employeeId?: string;
	departmentId?: string;
	zoneId?: string;
	status?: string;
}

export function useAttendanceDays(filters: AttendanceDayFilters) {
	const params: Record<string, string> = { from: filters.from, to: filters.to };

	// ⚠️ Bo'sh qiymatlar params ga QO'SHILMAYDI — `usePaginatedQuery`
	// `params` ni queryKey ga kiritadi, ya'ni `undefined` maydonlar
	// keraksiz qayta so'rovlarga olib kelardi.
	if (filters.employeeId) {
		params.employeeId = filters.employeeId;
	}
	if (filters.departmentId) {
		params.departmentId = filters.departmentId;
	}
	if (filters.zoneId) {
		params.zoneId = filters.zoneId;
	}
	if (filters.status) {
		params.status = filters.status;
	}

	return usePaginatedQuery<AttendanceDay>({
		queryKey: ["attendance-days"],
		url: "/attendance/days",
		params,
	});
}

/**
 * Kun tafsiloti.
 *
 * ⚠️ **Tuzatishdan keyin qisqa intervalda POLL qilinadi.** `attendance_days`
 * proyeksiyasi tuzatishga ASINXRON javob beradi (`recompute.store.ts` dagi
 * batafsil izoh), ya'ni darhol qilingan invalidatsiya ESKI qatorni qaytaradi.
 * Poll `computedAt` bazaviy qiymatdan chiqishi bilan to'xtaydi va shu paytda
 * RO'YXAT ham qayta invalidatsiya qilinadi: birinchi invalidatsiya uni ham
 * eskirgan holatda to'ldirgan, `staleTime: 5 daqiqa` esa o'z-o'zidan
 * tuzalishga yo'l qo'ymaydi.
 */
export function useAttendanceDay(id: string | null) {
	const queryClient = useQueryClient();
	const baseline = useRecomputeStore((state) => (id === null ? undefined : state.baselines[id]));
	const clearRecomputing = useRecomputeStore((state) => state.clearRecomputing);

	const query = useQuery({
		queryKey: ["attendance-day", id],
		queryFn: () => attendanceService.getDay(id as string),
		// Drawer yopiq bo'lganda so'rov yuborilmasin — poll ham shu bilan
		// tabiiy chegaraga ega bo'ladi.
		enabled: id !== null,
		refetchInterval: (q) => recomputePollInterval(baseline, q.state.data?.day.computedAt),
	});

	const computedAt = query.data?.day.computedAt;

	useEffect(() => {
		if (id === null || baseline === undefined || computedAt === undefined) {
			return;
		}

		if (computedAt === baseline) {
			return;
		}

		clearRecomputing(id);
		queryClient.invalidateQueries({ queryKey: ["attendance-days"] });
	}, [id, baseline, computedAt, clearRecomputing, queryClient]);

	return query;
}

/**
 * Tuzatish yozish.
 *
 * ⚠️⚠️ **201 «kun yangilandi» degani EMAS.** Marshrut faqat
 * `attendance_events` qatorini sinxron yozadi, `attendance_days` esa
 * `attendance` navbatidagi job orqali ASINXRON qayta quriladi. Shuning uchun:
 *
 * 1. Invalidatsiyadan OLDIN bazaviy `computedAt` yozib olinadi va kun
 *    «qayta hisoblanmoqda» holatiga o'tadi (`useAttendanceDay` uni poll
 *    qiladi, drawer esa aniq ogohlantirish ko'rsatadi).
 * 2. Xabar ham shu haqiqatni aytadi. Eski «Tuzatish qo'llandi» matni ORTIQCHA
 *    da'vo edi: rahbar tasmada yangi hodisani ko'rib, `status` /
 *    `lateMinutes` o'zgarmaganini sezgach tuzatishni QAYTA yuborardi va
 *    APPEND-ONLY jurnalga takroriy `departure` tushardi.
 */
export function useCreateCorrection(dayId: string | null) {
	const { message } = App.useApp();
	const { t } = useTranslation("attendance");
	const queryClient = useQueryClient();
	const markRecomputing = useRecomputeStore((state) => state.markRecomputing);

	return useMutation({
		mutationFn: (input: CorrectionInput) =>
			attendanceService.createCorrection(dayId as string, input),
		onSuccess: () => {
			const detail =
				dayId === null
					? undefined
					: queryClient.getQueryData<AttendanceDayDetail>(["attendance-day", dayId]);

			// ⚠️ Belgi invalidatsiyadan OLDIN qo'yiladi — aks holda qayta so'rov
			// pollsiz ketardi va eski qiymatlar qotib qolardi.
			if (dayId !== null && detail) {
				markRecomputing(dayId, detail.day.computedAt);
			}

			message.success(t("messages.correctionSaved"));
			queryClient.invalidateQueries({ queryKey: ["attendance-day", dayId] });
			queryClient.invalidateQueries({ queryKey: ["attendance-days"] });
		},
		onError: (error) => {
			message.error(getErrorMessage(error, t("messages.correctionSaveError")));
		},
	});
}

interface EmployeeOption {
	id: string;
	fullName: string;
}

/**
 * Xodim tanlash — server tomonida qidiriladi.
 *
 * ⚠️ Bu `modules/schedules` dagi bir xil nomli hookning NUSXASI. Modullar
 * bir-birining ichki hooklarini import qilmaydi (`src/modules/<x>` chegarasi);
 * umumiylashtirish kerak bo'lsa u `src/shared/hooks` ga ko'chiriladi — bu
 * esa D bloki bilan yana bir umumiy fayl demakdir (NUQSON-6), shuning uchun
 * 2-bosqichda nusxa qoldiriladi.
 */
export function useAttendanceEmployeeOptions(search: string) {
	return useQuery({
		queryKey: ["employees", "attendance-options", search],
		queryFn: async () => {
			const params: Record<string, string | number> = { page: 1, limit: 100 };

			if (search.trim()) {
				params.q = search.trim();
			}

			const response = await apiClient.get<{
				success: true;
				data: { items: EmployeeOption[] };
			}>("/employees", { params });

			return response.data.data.items;
		},
		staleTime: 30_000,
	});
}

/**
 * Bo'lim filtri opsiyalari.
 *
 * ⚠️ **Spec §6.5 filtrlarni ANIQ sanaydi: «sana + bo'lim/hudud filtri».**
 * Backend ularni allaqachon qo'llab-quvvatlaydi (`departmentId`, `zoneId` —
 * Task 7), UI'da bo'lmasa talab bajarilmagan bo'lardi.
 *
 * ⚠️ `GET /departments` sahifalanmagan va TEKIS ro'yxat qaytaradi
 * (`departments.service.ts` naqshi) — konvert `data` (massiv), `data.items`
 * EMAS.
 *
 * ⚠️ `retry: false` — ro'yxat `departments:read` ruxsatini talab qiladi va
 * kontrakt §10 dagi `manager` roli unga EGA EMAS. 403 kelganda filtr
 * jimgina bo'sh qoladi (`useEmployeeNames` dagi bir xil qaror), sahifa esa
 * ishlashda davom etadi.
 */
export function useAttendanceDepartmentOptions() {
	return useQuery({
		queryKey: ["attendance", "department-options"],
		queryFn: async () => {
			const response = await apiClient.get<{ success: true; data: { id: string; name: string }[] }>(
				"/departments"
			);

			return response.data.data;
		},
		staleTime: 5 * 60_000,
		retry: false,
	});
}

/**
 * Hudud filtri opsiyalari.
 *
 * ⚠️ `GET /zones` SAHIFALANGAN (`createPaginatedSchema`) — konvert
 * `data.items`. `isActive` standarti `"true"`, ya'ni arxivlangan hudud
 * ro'yxatga tushmaydi va bu to'g'ri: arxivlangan hududga bugun hech kim
 * biriktirilmagan.
 *
 * ⚠️ `retry: false` — `zones:read` ruxsati bo'lmagan rahbarda 403 keladi;
 * yuqoridagi izohga qarang.
 */
export function useAttendanceZoneOptions() {
	return useQuery({
		queryKey: ["attendance", "zone-options"],
		queryFn: async () => {
			const response = await apiClient.get<{
				success: true;
				data: { items: { id: string; name: string }[] };
			}>("/zones", { params: { page: 1, limit: 100 } });

			return response.data.data.items;
		},
		staleTime: 5 * 60_000,
		retry: false,
	});
}
